import { useEffect, useState } from 'react';
import { CheckSquare, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import type { PhaseWithStats, TemplateWithItems, UserSummary } from '@epasu/core';
import { crm, type TaskRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { formatDate } from '@/lib/utils';

interface Props {
  projectId: string;
  phases: PhaseWithStats[];
  tasks: TaskRow[];
  onChanged: () => void;
}

/**
 * Phasen des Projekts mit ihren Aufgaben.
 *
 * Tritt an die Stelle der früheren `TasksCard`, die eine flache Liste war.
 * Flach ging, solange ein Projekt ein Haufen To-dos war; sobald man die
 * Arbeit gliedern will, fehlte die Ebene dazwischen.
 *
 * Ein Projekt **ohne** Phasen sieht weiterhin aus wie vorher: nur die Liste,
 * dazu ein Knopf, der die erste Phase anlegt. Phasen sind ein Angebot, keine
 * Pflicht — sonst kostet jedes Zwei-Tage-Projekt Zeremonie.
 *
 * Verschoben wird über ein Auswahlfeld je Aufgabe, nicht über Ziehen. Ziehen
 * sieht besser aus, braucht aber eine Tastaturalternative, damit es
 * bedienbar bleibt; das Auswahlfeld ist beides in einem.
 */
export function PhasesCard({ projectId, phases, tasks, onChanged }: Props) {
  const [neuePhase, setNeuePhase] = useState('');
  const [phaseFormOffen, setPhaseFormOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [vorlagen, setVorlagen] = useState<TemplateWithItems[]>([]);
  const [konten, setKonten] = useState<UserSummary[]>([]);

  // Vorlagen und Konten aendern sich selten und haengen nicht am Projekt —
  // einmal beim Oeffnen reicht, ein Nachladen bei jedem Abhaken waere
  // Verschwendung.
  useEffect(() => {
    void crm.projectTemplates.list().then(setVorlagen).catch(() => setVorlagen([]));
    void crm.users.list().then(setKonten).catch(() => setKonten([]));
  }, []);

  const melden = (err: unknown, fallback: string) =>
    setFehler(err instanceof Error ? err.message : fallback);

  const phaseAnlegen = async () => {
    const titel = neuePhase.trim();
    if (!titel) return;
    setBusy(true);
    setFehler(null);
    try {
      await crm.phases.create({ projectId, title: titel });
      setNeuePhase('');
      setPhaseFormOffen(false);
      onChanged();
    } catch (err) {
      melden(err, 'Phase anlegen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const vorlageAnwenden = async (templateId: string) => {
    if (!templateId) return;
    setBusy(true);
    setFehler(null);
    try {
      await crm.projects.applyTemplate(projectId, templateId);
      onChanged();
    } catch (err) {
      melden(err, 'Vorlage anwenden fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const phaseAbhaken = async (phase: PhaseWithStats) => {
    setFehler(null);
    try {
      await crm.phases.setDone(phase.id, phase.doneAt === null);
      onChanged();
    } catch (err) {
      melden(err, 'Phase ändern fehlgeschlagen');
    }
  };

  const phaseLoeschen = async (phase: PhaseWithStats) => {
    setFehler(null);
    try {
      await crm.phases.delete(phase.id);
      onChanged();
    } catch (err) {
      melden(err, 'Phase löschen fehlgeschlagen');
    }
  };

  const ohnePhase = tasks.filter((t) => !t.phaseId);

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <CheckSquare className="h-4 w-4 text-muted-foreground" />
          Phasen &amp; Aufgaben
        </h2>
        <div className="flex items-center gap-2">
          {/* Die Auswahl erscheint nur, wenn es Vorlagen gibt — ein leeres
              Feld „Vorlage“ erklärt nichts und fragt trotzdem nach Platz. */}
          {vorlagen.length > 0 && (
            <Select
              className="h-8 w-40 text-xs"
              value=""
              onChange={(e) => void vorlageAnwenden(e.target.value)}
              aria-label="Vorlage anwenden"
              disabled={busy}
            >
              <option value="">Vorlage anwenden …</option>
              {vorlagen.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPhaseFormOffen((o) => !o)}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Phase
          </Button>
        </div>
      </div>

      {phaseFormOffen && (
        <div className="mt-3 flex gap-2">
          <Input
            autoFocus
            value={neuePhase}
            onChange={(e) => setNeuePhase(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setPhaseFormOffen(false);
              if (e.key !== 'Enter') return;
              e.preventDefault();
              void phaseAnlegen();
            }}
            placeholder="Konzept, Umsetzung, Abnahme …"
            disabled={busy}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Phase anlegen"
            onClick={() => void phaseAnlegen()}
            disabled={busy || !neuePhase.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}

      {phases.length === 0 && ohnePhase.length === 0 && !phaseFormOffen && (
        <p className="mt-2 text-sm text-muted-foreground">
          Noch keine Aufgabe. „Phase“ gliedert die Arbeit in Abschnitte — für ein
          kleines Projekt reicht die Liste darunter.
        </p>
      )}

      <div className="mt-3 space-y-3">
        {phases.map((phase, i) => (
          <PhaseBlock
            key={phase.id}
            phase={phase}
            nummer={i + 1}
            gesamt={phases.length}
            projectId={projectId}
            phases={phases}
            konten={konten}
            tasks={tasks.filter((t) => t.phaseId === phase.id)}
            onAbhaken={() => void phaseAbhaken(phase)}
            onLoeschen={() => void phaseLoeschen(phase)}
            onChanged={onChanged}
            onFehler={melden}
          />
        ))}

        <PhaseBlock
          phase={null}
          projectId={projectId}
          phases={phases}
          konten={konten}
          tasks={ohnePhase}
          onChanged={onChanged}
          onFehler={melden}
        />
      </div>

      {fehler && <p className="mt-2 text-xs text-danger">{fehler}</p>}
    </Card>
  );
}

/**
 * Ein Abschnitt mit seinen Aufgaben. `phase === null` ist „Ohne Phase“ — der
 * Sammelplatz, der auch ohne jede Phase existiert und beim Löschen einer
 * Phase ihre Aufgaben auffängt.
 */
function PhaseBlock({
  phase,
  nummer,
  gesamt,
  projectId,
  phases,
  konten,
  tasks,
  onAbhaken,
  onLoeschen,
  onChanged,
  onFehler,
}: {
  phase: PhaseWithStats | null;
  nummer?: number;
  gesamt?: number;
  projectId: string;
  phases: PhaseWithStats[];
  konten: UserSummary[];
  tasks: TaskRow[];
  onAbhaken?: () => void;
  onLoeschen?: () => void;
  onChanged: () => void;
  onFehler: (err: unknown, fallback: string) => void;
}) {
  const [offen, setOffen] = useState(true);
  const [titel, setTitel] = useState('');
  const [busy, setBusy] = useState(false);

  // „Ohne Phase“ ohne Aufgaben und ohne vorhandene Phasen waere eine leere
  // Ueberschrift ueber einem leeren Feld — dann lieber nur das Eingabefeld.
  const nurEingabe = phase === null && tasks.length === 0 && phases.length > 0;

  const anlegen = async () => {
    const name = titel.trim();
    if (!name) return;
    setBusy(true);
    try {
      await crm.tasks.create({ title: name, projectId, phaseId: phase?.id ?? null });
      setTitel('');
      onChanged();
    } catch (err) {
      onFehler(err, 'Anlegen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const abhaken = async (taskId: string) => {
    try {
      await crm.tasks.setStatus(taskId, 'erledigt');
      onChanged();
    } catch (err) {
      onFehler(err, 'Erledigen fehlgeschlagen');
    }
  };

  const verschieben = async (taskId: string, phaseId: string) => {
    try {
      await crm.tasks.update(taskId, { phaseId: phaseId || null });
      onChanged();
    } catch (err) {
      onFehler(err, 'Verschieben fehlgeschlagen');
    }
  };

  const zuweisen = async (taskId: string, userId: string) => {
    try {
      await crm.tasks.update(taskId, { assigneeUserId: userId || null });
      onChanged();
    } catch (err) {
      onFehler(err, 'Zuweisen fehlgeschlagen');
    }
  };

  if (nurEingabe) return null;

  return (
    <div className={phase ? 'rounded-md border border-border' : ''}>
      {phase ? (
        <div className="flex items-center gap-2 px-2 py-1.5">
          <button
            type="button"
            onClick={() => setOffen((o) => !o)}
            className="text-muted-foreground hover:text-foreground"
            aria-label={offen ? 'Phase einklappen' : 'Phase ausklappen'}
          >
            {offen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          <input
            type="checkbox"
            className="h-4 w-4"
            checked={phase.doneAt !== null}
            onChange={onAbhaken}
            aria-label={`Phase ${phase.title} abhaken`}
          />

          <span className="min-w-0 flex-1">
            <span
              className={`block text-sm font-medium ${
                phase.erledigt ? 'text-muted-foreground line-through' : ''
              }`}
            >
              {phase.title}
            </span>
            <span className="block text-xs text-muted-foreground">
              Phase {nummer} von {gesamt}
              {phase.totalTasks > 0 &&
                ` · ${phase.totalTasks - phase.openTasks} von ${phase.totalTasks} erledigt`}
              {phase.dueDate && ` · bis ${formatDate(phase.dueDate)}`}
            </span>
          </span>

          <button
            type="button"
            onClick={onLoeschen}
            className="text-muted-foreground hover:text-danger"
            aria-label={`Phase ${phase.title} löschen`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        tasks.length > 0 && (
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Ohne Phase
          </p>
        )
      )}

      {(!phase || offen) && (
        <div className={phase ? 'border-t border-border px-2 py-2' : 'mt-2'}>
          {tasks.length > 0 && (
            <ul className="space-y-1.5">
              {tasks.map((t) => (
                <li key={t.id} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    aria-label={`${t.title} erledigen`}
                    onChange={() => void abhaken(t.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block">{t.title}</span>
                    {(t.dueAt || t.assigneeName) && (
                      <span className="block text-xs text-muted-foreground">
                        {t.dueAt && `Fällig ${formatDate(t.dueAt)}`}
                        {t.dueAt && t.assigneeName && ' · '}
                        {t.assigneeName}
                      </span>
                    )}
                  </span>
                  {/* Die Zuweisung erscheint erst ab zwei Konten. Bei
                      einem waere jede Zeile eine Auswahl mit genau einer
                      Moeglichkeit — Pflegeaufwand ohne Aussage. */}
                  {konten.length > 1 && (
                    <Select
                      className="h-7 w-28 text-xs"
                      value={t.assigneeUserId ?? ''}
                      onChange={(e) => void zuweisen(t.id, e.target.value)}
                      aria-label={`${t.title} zuweisen`}
                    >
                      <option value="">Niemand</option>
                      {konten.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.name?.trim() || k.email}
                        </option>
                      ))}
                    </Select>
                  )}
                  {phases.length > 0 && (
                    <Select
                      className="h-7 w-28 text-xs"
                      value={t.phaseId ?? ''}
                      onChange={(e) => void verschieben(t.id, e.target.value)}
                      aria-label={`${t.title} verschieben`}
                    >
                      <option value="">Ohne Phase</option>
                      {phases.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title}
                        </option>
                      ))}
                    </Select>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className={`flex gap-2 ${tasks.length > 0 ? 'mt-2' : ''}`}>
            <Input
              className="h-8 text-sm"
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                void anlegen();
              }}
              placeholder={phase ? `Aufgabe in „${phase.title}“ …` : 'Neue Aufgabe …'}
              disabled={busy}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0"
              aria-label="Aufgabe anlegen"
              onClick={() => void anlegen()}
              disabled={busy || !titel.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
